import React, { useState, useEffect, useRef } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { apiUrl } from '../config/api';
import { fuzzyAddressMatch } from '../lib/addressMatch';
import './LocationProjectModal.css';

// Custom pin instead of Leaflet's default marker images — sidesteps the well-known
// webpack/CRA asset-path breakage with Leaflet's default icon, and lets the "found you" moment
// read as an animation (CSS pulse + drop-in) rather than a static pin.
const technicianIcon = L.divIcon({
  className: 'lpm-pin-wrapper',
  html: '<div class="lpm-pin-pulse"></div><div class="lpm-pin-dot"></div>',
  iconSize: [32, 32],
  iconAnchor: [16, 16],
});

// One flow, three entry points (Create Project / Take Photos / Upload Photos on the Home
// dashboard): detect where the technician is standing, check it against an existing project
// first (local clients, then — since GHL can only search by name/email/phone, never
// address — contacts behind the currently-synced appointment window), and only fall back to
// "create a brand new project" once both come up empty.
// A phone answers getCurrentPosition with whatever it has to hand — usually a coarse
// wifi/cell fix accurate to hundreds of metres — and only refines to a real GPS fix a
// few seconds later. Taking the first answer is what put technicians on the wrong
// street. So we watch the position and keep the best fix, stopping as soon as it is
// good enough or when the deadline passes.
const GOOD_ACCURACY_M = 25;      // a GPS fix this tight is the right driveway
const LOCATE_DEADLINE_MS = 12000; // past this, take the best we have rather than stall
// Beyond this the fix is a neighbourhood, not an address — say so rather than
// silently reverse-geocoding the wrong street.
const VAGUE_ACCURACY_M = 150;

const LocationProjectModal = ({ isOpen, onClose, intent, clients, token, onResolved }) => {
  const [step, setStep] = useState('locating'); // locating | error | searching | confirm-local | confirm-ghl | create-new | resolving
  const [coords, setCoords] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [refining, setRefining] = useState(false);
  const [address, setAddress] = useState('');
  const [localMatch, setLocalMatch] = useState(null);
  const [ghlMatch, setGhlMatch] = useState(null);
  const [newName, setNewName] = useState('');
  const [newAddress, setNewAddress] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const watchRef = useRef(null);
  const watchTimerRef = useRef(null);

  const intentLabel = intent === 'create' ? 'Create Project' : 'Take/Upload Photos';

  // Reset on every open — this modal is mounted once and reused, not remounted per open.
  useEffect(() => {
    if (!isOpen) return;
    setStep('locating');
    setCoords(null);
    setAddress('');
    setLocalMatch(null);
    setGhlMatch(null);
    setNewName('');
    setNewAddress('');
    setErrorMessage('');

    setAccuracy(null);

    if (!navigator.geolocation) {
      setErrorMessage('This device/browser can’t provide a location.');
      setStep('error');
      return undefined;
    }

    let best = null;
    const stop = () => {
      if (watchRef.current != null) navigator.geolocation.clearWatch(watchRef.current);
      watchRef.current = null;
      if (watchTimerRef.current) clearTimeout(watchTimerRef.current);
      watchTimerRef.current = null;
      setRefining(false);
    };

    const accept = () => {
      if (!best) return;
      stop();
      setCoords({ lat: best.coords.latitude, lon: best.coords.longitude });
      setAccuracy(best.coords.accuracy ?? null);
    };

    setRefining(true);
    watchRef.current = navigator.geolocation.watchPosition(
      (position) => {
        // Keep the tightest fix seen. Accuracy generally improves as the GPS warms
        // up, but a later reading can be worse, and that must not undo a good one.
        if (!best || (position.coords.accuracy ?? Infinity) < (best.coords.accuracy ?? Infinity)) {
          best = position;
        }
        if ((best.coords.accuracy ?? Infinity) <= GOOD_ACCURACY_M) accept();
      },
      (err) => {
        // A timeout with a usable fix already in hand is a success, not a failure.
        if (best) { accept(); return; }
        stop();
        setErrorMessage(err.message || 'Location access was denied.');
        setStep('error');
      },
      // maximumAge: 0 is the important one. The old value (60s) let the browser hand
      // back a cached fix from the previous address, which is how a technician who had
      // just driven across town got the street they came from.
      { enableHighAccuracy: true, timeout: LOCATE_DEADLINE_MS, maximumAge: 0 }
    );

    watchTimerRef.current = setTimeout(() => {
      if (best) accept();
      else {
        stop();
        setErrorMessage('Couldn’t get a location fix. Move somewhere with a clearer view of the sky and try again.');
        setStep('error');
      }
    }, LOCATE_DEADLINE_MS);

    return stop;
  }, [isOpen]);

  // Once we have coordinates: paint the map, reverse-geocode, then search local clients and
  // (only if nothing local matched) GHL's recently-synced appointment contacts.
  useEffect(() => {
    if (!coords) return;

    (async () => {
      setStep('searching');
      try {
        const response = await fetch(apiUrl(`/api/geocode/reverse?lat=${coords.lat}&lon=${coords.lon}`), {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        const resolvedAddress = data.address || `${coords.lat.toFixed(5)}, ${coords.lon.toFixed(5)}`;
        setAddress(resolvedAddress);
        setNewAddress(resolvedAddress);

        const local = (clients || []).find(
          (c) => c.clientAddress && fuzzyAddressMatch(c.clientAddress, resolvedAddress)
        );
        if (local) {
          setLocalMatch(local);
          setStep('confirm-local');
          return;
        }

        try {
          const ghlResponse = await fetch(
            apiUrl(`/api/ghl/match-appointment-by-address?address=${encodeURIComponent(resolvedAddress)}`),
            { headers: { Authorization: `Bearer ${token}` } }
          );
          const ghlData = await ghlResponse.json();
          if (ghlData.match) {
            setGhlMatch(ghlData.match);
            setStep('confirm-ghl');
            return;
          }
        } catch {
          // Non-fatal — GHL being unreachable just means we fall through to "create new" below.
        }

        setStep('create-new');
      } catch (err) {
        setErrorMessage('Could not resolve an address for your location.');
        setStep('create-new'); // still usable — technician can type the address by hand
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords]);

  // Paint/update the map whenever we have coordinates and the map pane is mounted.
  useEffect(() => {
    if (!coords || !mapContainerRef.current) return;

    if (!mapRef.current) {
      mapRef.current = L.map(mapContainerRef.current, {
        zoomControl: false,
        attributionControl: false,
      }).setView([coords.lat, coords.lon], 17);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
      }).addTo(mapRef.current);
      L.marker([coords.lat, coords.lon], { icon: technicianIcon }).addTo(mapRef.current);
    } else {
      mapRef.current.setView([coords.lat, coords.lon], 17);
    }

    // Leaflet measures its container on init; if that happened while the modal was still
    // animating in (or the container was display:none), the tile grid comes out wrong-sized.
    setTimeout(() => mapRef.current?.invalidateSize(), 200);
  }, [coords]);

  useEffect(() => {
    if (!isOpen && mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
    }
  }, [isOpen]);

  const handleConfirmLocal = () => {
    onResolved(localMatch, intent);
    onClose();
  };

  const handleConfirmGhl = async () => {
    setStep('resolving');
    try {
      const response = await fetch(apiUrl(`/api/ghl/appointments/${ghlMatch.contactId}/open`), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ contactId: ghlMatch.contactId }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to link this GHL contact');
      onResolved({ id: data.clientId, clientName: ghlMatch.name, clientAddress: ghlMatch.address }, intent);
      onClose();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to link this GHL contact');
      setStep('confirm-ghl');
    }
  };

  const handleCreateNew = async () => {
    if (!newName.trim()) {
      setErrorMessage('Enter a name for this project.');
      return;
    }
    setStep('resolving');
    try {
      const response = await fetch(apiUrl('/api/scraper/create-client'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ clientName: newName.trim(), clientAddress: newAddress.trim() }),
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.message || 'Failed to create project');
      onResolved({ id: data.clientId, clientName: newName.trim(), clientAddress: newAddress.trim() }, intent);
      onClose();
    } catch (err) {
      setErrorMessage(err.message || 'Failed to create project');
      setStep('create-new');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="lpm-overlay" onClick={onClose}>
      <div className="lpm-modal" onClick={(e) => e.stopPropagation()}>
        <div className="lpm-header">
          <h2 className="lpm-title">{intentLabel}</h2>
          <button className="lpm-close" onClick={onClose} aria-label="Close">&#x2715;</button>
        </div>

        <div ref={mapContainerRef} className="lpm-map" />
        {address && <p className="lpm-detected-address">📍 {address}</p>}

        <div className="lpm-body">
          {step === 'locating' && (
            <div className="lpm-status">
              <div className="lpm-spinner" />
              {refining ? 'Getting an accurate fix…' : 'Finding your location…'}
            </div>
          )}

          {/* How good the fix is, in plain terms. A technician who can see "±180 m"
              knows to retry before accepting the wrong street; silently showing a
              reverse-geocoded address from a vague fix is what caused the complaint. */}
          {accuracy != null && step !== 'locating' && step !== 'error' && (
            <p className={`lpm-accuracy${accuracy > VAGUE_ACCURACY_M ? ' lpm-accuracy--vague' : ''}`}>
              {accuracy > VAGUE_ACCURACY_M
                ? `Rough location only (±${Math.round(accuracy)} m) — check the address below`
                : `Located to about ±${Math.round(accuracy)} m`}
            </p>
          )}

          {step === 'error' && (
            <div className="lpm-status">
              <p className="lpm-error-text">{errorMessage}</p>
              <button className="lpm-btn lpm-btn--secondary" onClick={() => setStep('create-new')}>
                Enter address manually
              </button>
            </div>
          )}

          {step === 'searching' && (
            <div className="lpm-status">
              <div className="lpm-spinner" />
              Checking for an existing project at your location&hellip;
            </div>
          )}

          {step === 'resolving' && (
            <div className="lpm-status">
              <div className="lpm-spinner" />
              One moment&hellip;
            </div>
          )}

          {step === 'confirm-local' && localMatch && (
            <div className="lpm-confirm">
              <p className="lpm-confirm-label">You're at an existing project:</p>
              <p className="lpm-confirm-name">{localMatch.clientName}</p>
              <p className="lpm-confirm-address">{localMatch.clientAddress}</p>
              <div className="lpm-btn-row">
                <button className="lpm-btn lpm-btn--primary" onClick={handleConfirmLocal}>
                  Yes, continue with this project
                </button>
                <button className="lpm-btn lpm-btn--secondary" onClick={() => setStep('create-new')}>
                  No, it's something else
                </button>
              </div>
            </div>
          )}

          {step === 'confirm-ghl' && ghlMatch && (
            <div className="lpm-confirm">
              <p className="lpm-confirm-label">Found a matching GoHighLevel contact:</p>
              <p className="lpm-confirm-name">{ghlMatch.name}</p>
              <p className="lpm-confirm-address">{ghlMatch.address}</p>
              {errorMessage && <p className="lpm-error-text">{errorMessage}</p>}
              <div className="lpm-btn-row">
                <button className="lpm-btn lpm-btn--primary" onClick={handleConfirmGhl}>
                  Yes, create project for them
                </button>
                <button className="lpm-btn lpm-btn--secondary" onClick={() => setStep('create-new')}>
                  No, it's something else
                </button>
              </div>
            </div>
          )}

          {step === 'create-new' && (
            <div className="lpm-confirm">
              <p className="lpm-confirm-label">No existing project found at this location — create one?</p>
              <div className="lpm-form-group">
                <label className="lpm-label">Project / Client Name</label>
                <input
                  type="text"
                  className="lpm-input"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  placeholder="Customer or project name"
                  autoFocus
                />
              </div>
              <div className="lpm-form-group">
                <label className="lpm-label">Address</label>
                <input
                  type="text"
                  className="lpm-input"
                  value={newAddress}
                  onChange={(e) => setNewAddress(e.target.value)}
                  placeholder="Street address"
                />
              </div>
              {errorMessage && <p className="lpm-error-text">{errorMessage}</p>}
              <button className="lpm-btn lpm-btn--primary lpm-btn--full" onClick={handleCreateNew}>
                Create Project
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LocationProjectModal;
